import React, { useEffect, useState } from "react";
import { HiOutlineTrash, HiOutlinePencilSquare, HiOutlineQueueList, HiPlus } from "react-icons/hi2";
import { FaSearch } from "react-icons/fa";

import { StorageUnit } from "../../types";
import { RenderDeleteUnitModal, RenderCreateUnitModal, RenderEditUnitModal } from "../RenderFunctions";
import StorageStock from "./components/StorageUnits";

interface TableProps {
  data: StorageUnit[];
  setData: (data: StorageUnit[]) => void;
}

const UnitsTable: React.FC<TableProps> = ({ data, setData }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentUnit, setCurrentUnit] = useState<StorageUnit | null>(null);
  const [showStockModal, setShowStockModal] = useState(false);
  const [currentStockUnitId, setCurrentStockUnitId] = useState<string>("");
  const [searchTerm, setSearchTerm] = useState("");
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 900);
    handleResize();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const dataMap = data.reduce((acc: { [key: number]: string }, unit) => {
    acc[unit.storage_unit_id] = unit.name;
    return acc;
  }, {});

  const filteredData = data.filter((u) =>
    !searchTerm.trim() || u.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleShowEditModal = (unit: StorageUnit) => {
    setCurrentUnit(unit);
    setShowEditModal(true);
  };

  const handleShowDeleteModal = (unit: StorageUnit) => {
    setCurrentUnit(unit);
    setShowDeleteModal(true);
  };

  const handleShowStockModal = (unit: StorageUnit) => {
    setCurrentStockUnitId(unit.storage_unit_id.toString());
    setShowStockModal(true);
  };

  return (
    <>
      {/* Toolbar */}
      <div className="mp-toolbar">
        <div className="flex flex-wrap items-center gap-2 lg:gap-3">
          <div className="flex-1 min-w-[180px] lg:min-w-[220px] max-w-[380px] relative">
            <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-xs opacity-40" style={{ color: 'var(--text-color)' }} />
            <input
              type="text"
              placeholder="Buscar unidades..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="mp-form-input"
              style={{ paddingLeft: '2rem' }}
            />
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button className="mp-btn-primary" onClick={() => setShowCreateModal(true)}>
              <HiPlus style={{ marginRight: '0.375rem' }} />
              Crear Unidad
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mp-table-card">
        <div style={{ overflowX: 'auto' }}>
          <table className="mp-table">
            <thead className="mp-table-head">
              <tr>
                <th>Nombre</th>
                {!isMobile && <th style={{ textAlign: 'center' }}>Global</th>}
                {!isMobile && <th style={{ textAlign: 'center' }}>Prioridad</th>}
                <th>Respaldo</th>
                <th style={{ textAlign: 'center' }}>Acciones</th>
              </tr>
            </thead>
            <tbody className="mp-table-body">
              {filteredData.length === 0 ? (
                <tr>
                  <td colSpan={isMobile ? 3 : 5}>
                    <div className="mp-empty-state">
                      <span className="mp-empty-state-title">No se encontraron unidades</span>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredData.map((u: StorageUnit) => (
                  <tr key={u.storage_unit_id}>
                    <td>{u.name}</td>
                    {!isMobile && (
                      <td style={{ textAlign: 'center' }}>
                        <span className={`mp-badge ${u.is_global ? 'mp-badge-success' : 'mp-badge-neutral'}`}>
                          {u.is_global ? 'Sí' : 'No'}
                        </span>
                      </td>
                    )}
                    {!isMobile && <td style={{ textAlign: 'center' }}>{u.priority}</td>}
                    <td>
                      {dataMap[u.backup_storage_unit_id!] || (
                        <span className="mp-badge mp-badge-neutral">N/A</span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <div className="flex items-center justify-center gap-1.5">
                        <button className="mp-btn-icon" onClick={() => handleShowStockModal(u)} title="Movimientos">
                          <HiOutlineQueueList size={16} />
                        </button>
                        <button className="mp-btn-icon" onClick={() => handleShowEditModal(u)} title="Editar">
                          <HiOutlinePencilSquare size={16} />
                        </button>
                        <button className="mp-btn-icon mp-btn-icon-danger" onClick={() => handleShowDeleteModal(u)} title="Eliminar">
                          <HiOutlineTrash size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showCreateModal && (
        <RenderCreateUnitModal
          show={showCreateModal}
          handleClose={() => setShowCreateModal(false)}
          units={data}
          setUnits={setData}
        />
      )}
      {showEditModal && currentUnit && (
        <RenderEditUnitModal
          show={showEditModal}
          handleClose={() => setShowEditModal(false)}
          units={data}
          setUnits={setData}
          parentUnit={currentUnit}
        />
      )}
      {showDeleteModal && currentUnit && (
        <RenderDeleteUnitModal
          show={showDeleteModal}
          handleClose={() => setShowDeleteModal(false)}
          units={data}
          setUnits={setData}
          parentUnit={currentUnit}
        />
      )}
      {showStockModal && (
        <StorageStock
          show={showStockModal}
          onClose={() => setShowStockModal(false)}
          storageID={currentStockUnitId}
        />
      )}
    </>
  );
};

export default UnitsTable;
